import { Inject } from '@odinjs/odin';
import { BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, InputAPI, ModalButton, ModalProps, ModalService, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService,     } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, CredentialsValidationError, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';

import { Endpoint } from '../../../../constants/endpoints';
import BaseController from '../../../BaseController';
import { PendingExamsModalId } from '../send-container/send-container-controller';
import { FragmentNames, SendPrescriptionAliases } from '../../../../codegen';

import './send-prescription-controller.css';
import BarcodeService from '../../../../services/barcode-service';

export interface Exams {
  dtPrevExecucao: string,
  ieStatusAtend: number,
  nmExame: string,
  nmPaciente: string,
  nrPrescricao: number,
  nrSeqExame: number,
  nrSequencia: number
  statusDescription?: string,
}

export interface ExamsResponse{
  containsCollectedExam: boolean,
  exams: Exams[]
}

export interface SelectedBarcodeExam {
  barcode: string;
  exam: Exams;
}

let remainingTries = 3;

@Controller({ component: SendPrescriptionAliases.SEND_PRESCRIPTION_LAYOUT, fragment: FragmentNames.SEND_PRESCRIPTION})
class SendPrescriptionController extends BaseController {

  @Inject(SendPrescriptionAliases.SEND_PRESCRIPTION_PANEL)
  private sendPrescriptionPanelApi!: PanelAPI;

  @Inject(SendPrescriptionAliases.INPUT_PRESCRIPTION)
  private input!: InputAPI;

  @Inject(SendPrescriptionAliases.SEND_EXAMS_DECK)
  private deckApi!: DeckAPI;

  @Inject(SendPrescriptionAliases.BUTTON_SEND_CONTAINER)
  private buttonApi!: ButtonAPI;

  @OnMount({ target: SendPrescriptionAliases.SEND_PRESCRIPTION_PANEL })
  onPrescriptionPanelMount(): void {
    const prescription = this.historyService.getCurrentRouteParams().prescription;
    prescription === 'undefined' ? this.sendPrescriptionPanelApi.setVisible(false) : this.input.setValue(prescription as string);
  }

  private getExamsPrescription(value: string): void {
    const prescriptionNumber = Number(value);

    this.deckApi.setLoading(true);

    HttpService.get(Endpoint.GET_EXAMS_PRESCRIPTION, {prescriptionNumber})
      .then((response) => {
        const examsResponse = response.body as ExamsResponse;
        if (examsResponse.exams.length === 0) {
          this.deckApi.setLoading(false);
          this.showToastExamNotFound();
          return;
        }
        if (!examsResponse.containsCollectedExam) {
          this.showToastExamNotFoundCollected(prescriptionNumber);
          this.deckApi.setLoading(false);
          return;
        }

        const previousSelectedBarcodeExam: SelectedBarcodeExam[] = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];
        const selectExams: SelectedBarcodeExam[] = [];

        examsResponse.exams.forEach(item => {
          if (previousSelectedBarcodeExam === undefined || this.verifyPrescrptionInCard(previousSelectedBarcodeExam, examsResponse.exams)) {
            selectExams.push({
              barcode: item.nrPrescricao.toString(),
              exam: item,
            });
          }
        });

        if (previousSelectedBarcodeExam != null) {
          previousSelectedBarcodeExam.forEach(item => selectExams.push(item));
        }
        this.context.set({selectedBarcodeExams: selectExams});

        this.updateDeckFromContext();
        this.deckApi.setLoading(false);
      }).catch(() => {
        this.deckApi.setLoading(false);
        this.showToastExamNotFound();
      });
  }

  @OnMount(SendPrescriptionAliases.BARCODE_EXAM_CONTAINER)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess({ target: SendPrescriptionAliases.BARCODE_EXAM_CONTAINER })
  readSuccess(event: ReadEvent<BarcodeData>):void {
    this.getExamsPrescription(event.item.value.trim());
  }

  verifyPrescrptionInCard(previousSelectedBarcodeExam: SelectedBarcodeExam[], exams: Exams[]): boolean {
    let existCard = true;
    exams.forEach(item => {
      previousSelectedBarcodeExam.filter((preveiousItem: SelectedBarcodeExam) => {
        if (preveiousItem.barcode == item.nrPrescricao.toString()) {
          existCard = false;
          return;
        }
      })
    });

    return existCard;
  }

  showToastExamNotFound(): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'pending.exam-not-found',
      level: ToastLevel.WARNING,
      message: 'pending.exam-not-found',
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  showToastExamNotFoundCollected(nrPrescricao: number): void {
    const message = MessageService.translate('tws.txt-not-found-collected-exam').replace('#@NR_PRESCRICAO#@', `${nrPrescricao}`);
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'tws.txt-not-found-collected-exam',
      level: ToastLevel.WARNING,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  updateDeckFromContext(): void {
    const selectedBarcodeExams = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];
    const onResetClick = this.onResetClick;

    const groupedSelectedExams = this.groupData(selectedBarcodeExams);
    const keyMapper = (key: string): Record<string, Value> => ({
      cardKey: key,
      exams: groupedSelectedExams[key],
      onResetClick,
      title: this.getGroupTitle(groupedSelectedExams[key]),
    });
    const groupedSelectedExamsArr = Object.keys(groupedSelectedExams).map(keyMapper);

    this.deckApi.setData(groupedSelectedExamsArr);

    groupedSelectedExamsArr.length > 0 ? this.buttonApi.setEnabled(true): this.buttonApi.setEnabled(false);
  }

  groupData(data: SelectedBarcodeExam[]): Record<string, Exams[]> {
    return data.reduce<Record<string, Exams[]>>((accumulator, value) => {
      const groupByFieldValue = String(value.barcode);

      if (!groupByFieldValue) {
        return accumulator;
      }

      const oldGroupValue = accumulator[groupByFieldValue] || [];
      oldGroupValue.push(value.exam);
      accumulator[groupByFieldValue] = oldGroupValue;

      return accumulator;
    }, {});
  }

  getGroupTitle(examList: Exams[]): string {
    if(examList && examList.length > 0){
      return `${examList[0].nrPrescricao.toString()} - ${examList[0].nmPaciente}`;
    }
    return '';
  }

  onResetClick(evt: ClickEvent): void {
    const removedCardKey = evt.value?.cardKey;

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.form.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.removeExams(removedCardKey),
      title: 'tws.wizard.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialog: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'tws.selected-pending-exams.remove-exam-confirmation',
      dockButtons: buttons,
      focusOnOpen: true,
      onCancel: () => { },
      onSubmit: () => { },
      title: 'text.message-alert-attention',
      type: ModalType.DIALOG,
      uuid: generateUUID(),
    }

    ModalService.openDialog(dialog);
  }

  removeExams(cardKey: string): void {
    const currentSelected = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];

    const updatedSelected = currentSelected.filter((selected: SelectedBarcodeExam) => selected.barcode !== cardKey);

    this.context.set({selectedBarcodeExams: updatedSelected});

    this.updateDeckFromContext();

    if(updatedSelected.length === 0) {
      ModalService.close(PendingExamsModalId)
    }
    this.showRemoveExamSuccess(cardKey);
  }

  showRemoveExamSuccess(title: string): void {
    const toastId = 'remove-exam-success-toast' + title;
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: ToastLevel.SUCCESS,
      message: MessageService.translate('tws.selected-pending-exams.remove-exam-success'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000
    });
  }

  @OnClick({ target: SendPrescriptionAliases.BUTTON_SEND_CONTAINER })
  onClickButtonSend(): void {
    this.redirectCredentialValidation();
  }

  redirectCredentialValidation(): void {
    foundationContext.promptCredentialsValidation({
      onDismiss: (): void => {},
      onError: (event) => this.validateCredentialsWithError(event),
      onSuccess: () => this.validateCredentialsWithSuccess(),
      validateButtonExpression: MessageService.translate('$expression.$702951')
    });
  }

  validateCredentialsWithError(event: CredentialsValidationError): void {
    const { abort } = event;
    this.showToast(MessageService.translate('login-message.invalid-user-password-text'), ToastLevel.ERROR);

    if (remainingTries > 0) {
      remainingTries = remainingTries - 1;
    }

    if (remainingTries === 0) {
      remainingTries = 3;
      abort();
      applicationContext.popNavigationItem();
      applicationContext.popNavigationItem();
    }
  }

  validateCredentialsWithSuccess(): void {
    const currentSelected = this.context.get('selectedBarcodeExams') as SelectedBarcodeExam[];
    const listExams: Exams[] = [];
    currentSelected.forEach(item => {
      listExams.push(item.exam);
    })

    let recipientCode = 'null';
    if (this.sendPrescriptionPanelApi.isVisible()) {
      const aux = this.input.getValue().toString().split('-');
      recipientCode = aux[0].trim();
    }

    const body = {
      exams: listExams,
      recipientCode: recipientCode
    }

    HttpService.post(Endpoint.POST_COLLERT_EXAMS, {}, { body })
      .then(response => {
        const message = response.body;
        if (message == 597023) {
          this.showMessageExam('txt.error-exams-collected', ToastLevel.ERROR);
        } else {
          this.showMessageExam('txt.exams-collected', ToastLevel.SUCCESS);
        }

        applicationContext.popNavigationItem();
        applicationContext.popNavigationItem();
      })
      .catch(() => {
        this.showMessageExam('txt.error-exams-collected', ToastLevel.ERROR);
      });
  }

  showMessageExam(message: string, typeMessage: ToastLevel):void {
    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: 'return-exam-collected',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  showToast(message: string, typeMessage: ToastLevel): void {
    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: 'administration-by-process',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }
}

export default SendPrescriptionController;
